/* eslint-disable @typescript-eslint/no-explicit-any */
import { Button, Flex, Form, Spin } from "antd";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { useGetCoursesQuery } from "../../../app/features/admin/admin.api";
import {
  useCreatePromotionsMutation,
  useGeneratePromotionCodeMutation,
  useGetSinglePromotionQuery,
  useUpdatePromotionMutation,
} from "../../../app/features/adminPromotions/adminPromotions.api";
import type { PromotionsDto } from "../../../app/features/adminPromotions/adminPromotions.types";
import CustomTitle from "../../../components/CustomTitle";
import DashboardLayout from "../../../layouts/DashboardLayout";
import { toast } from "../../../lib/toast";
import BasicInfo from "./_components/BasicInfo";
import DiscountConfiguration from "./_components/DiscountConfiguration";
import OfferTypeSelect from "./_components/OfferTypeSelect";

// Helper to generate random code
const generateRandomCode = (length = 8) => {
  const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
  let result = "";
  for (let i = 0; i < length; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return result;
};

export default function ManageCampaign() {
  const [form] = Form.useForm();
  const { id, viewId } = useParams();
  const navigate = useNavigate();
  const [isGenerating, setIsGenerating] = useState(false);

  const isViewMode = !!viewId;
  const isUpdateMode = !!id;
  const campaignId = id || viewId;

  const pageTitle = isViewMode
    ? "View Campaign"
    : isUpdateMode
      ? "Update Campaign"
      : "Create Campaign";

  // APIs
  const { data: coursesData } = useGetCoursesQuery();
  const { data: campaignDetails, isLoading: isDetailsLoading } =
    useGetSinglePromotionQuery(campaignId as string, {
      skip: !campaignId,
    });

  const [createCampaign, { isLoading: isCreating }] =
    useCreatePromotionsMutation();
  const [updateCampaign, { isLoading: isUpdating }] =
    useUpdatePromotionMutation();
  const [generateCodeApi] = useGeneratePromotionCodeMutation();

  // Prepare course options
  const courseOptions =
    coursesData?.map((course) => ({
      label: course.title,
      value: course.uuid,
    })) || [];

  // Populate form in Edit/View mode
  useEffect(() => {
    if (campaignDetails && (isUpdateMode || isViewMode)) {
      form.setFieldsValue({
        title: campaignDetails.name,
        code: campaignDetails.code,
        offerType: campaignDetails.type
          ? campaignDetails.type.charAt(0).toUpperCase() +
            campaignDetails.type.slice(1).toLowerCase()
          : "Discount", // Simple mapping back to UI format
        startDate: campaignDetails.startsAt
          ? dayjs(campaignDetails.startsAt)
          : null,
        endDate: campaignDetails.endsAt ? dayjs(campaignDetails.endsAt) : null,
        discountType:
          campaignDetails.discountKind === "PERCENT" ? "percentage" : "amount",
        discountValue: campaignDetails.value,
        perUserLimit: campaignDetails.perUserLimit,
        maxBudget: campaignDetails.maxBudget,
        courses: campaignDetails.rules?.targets?.includeCourseUuids || [],
        isHidden: campaignDetails.isHidden || false,
      });
    }
  }, [campaignDetails, isUpdateMode, isViewMode, form]);

  const handleGenerateCode = async () => {
    setIsGenerating(true);
    try {
      if (isUpdateMode && campaignId) {
        // Use API for existing campaign
        const res = await generateCodeApi(campaignId).unwrap();
        form.setFieldValue("code", res.code);
        // toast("success", "Code generated successfully!");
      } else {
        // Generate random code client-side for new campaign
        const code = generateRandomCode();
        form.setFieldValue("code", code);
        // toast("success", "Code generated!");
      }
    } catch (error) {
      console.error(error);
      toast("error", "Failed to generate code");
    } finally {
      setIsGenerating(false);
    }
  };

  const onFinish = async (values: any) => {
    try {
      const payload: PromotionsDto = {
        name: values.title,
        code: values.code,
        type: values.offerType.toUpperCase(),
        status: "ACTIVE", // Default to ACTIVE
        scope: "ORDER", // Default scope
        discountKind:
          values.discountType === "percentage" ? "PERCENT" : "FIXED_AMOUNT",
        value: Number(values.discountValue),
        currency: "BDT",
        startsAt: values.startDate
          ? values.startDate.format("YYYY-MM-DD")
          : new Date().toISOString(),
        endsAt: values.endDate
          ? values.endDate.format("YYYY-MM-DD")
          : new Date().toISOString(),
        isHidden: values.isHidden || false,
        perUserLimit: Number(values.perUserLimit),
        maxBudget: Number(values.maxBudget),
        targets: {
          includeCourseUuids: values.courses || [],
          organizationUuid: "", // Optional/Global
          productType: "COURSE",
        },
        rules: {},
      };

      if (isUpdateMode && campaignId) {
        await updateCampaign({ body: payload, id: campaignId }).unwrap();
        toast("success", "Campaign updated successfully!");
      } else {
        await createCampaign(payload).unwrap();
        toast("success", "Campaign created successfully!");
      }
      navigate("/admin/campaign");
    } catch (error: any) {
      console.error(error);
      toast("error", error?.data?.message || "Something went wrong");
    }
  };

  if (isDetailsLoading) {
    return (
      <DashboardLayout title="Campaign">
        <CustomTitle
          title={pageTitle}
          description="Fill in all the details to create your campaign"
          items={[{ title: "Campaign" }, { title: pageTitle }]}
          showBackButton
        />
        <div className="py-5 flex justify-center items-center">
          <Spin size="large" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Campaign">
      <CustomTitle
        title={pageTitle}
        description="Fill in all the details to create your campaign"
        items={[{ title: "Campaign" }, { title: pageTitle }]}
        showBackButton
      />

      <Form
        form={form}
        layout="vertical"
        onFinish={onFinish}
        initialValues={{
          offerType: "Coupon",
          discountType: "amount",
          isHidden: false,
        }}
        className="max-w-5xl custom-form"
        disabled={isDetailsLoading}
        size="large"
      >
        <BasicInfo disabled={isViewMode} form={form} />
        <OfferTypeSelect disabled={isViewMode} />
        <DiscountConfiguration
          disabled={isViewMode}
          courseOptions={courseOptions}
          form={form}
          onGenerateCode={!isViewMode ? handleGenerateCode : undefined}
          isGenerating={isGenerating}
        />

        {!isViewMode && (
          <Flex gap={12} className="mt-8">
            <Button
              className="min-w-[120px]"
              size="large"
              onClick={() => navigate("/admin/campaign")}
            >
              Cancel
            </Button>
            <Button
              type="primary"
              htmlType="submit"
              className="min-w-[120px]"
              size="large"
              loading={isCreating || isUpdating}
            >
              {isUpdateMode ? "Update" : "Create"}
            </Button>
          </Flex>
        )}
      </Form>
    </DashboardLayout>
  );
}
